import type { SalesChecklistSection } from "@expert/contentful";

import { type JSX } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { sendSalesErrorMetric } from "../../../../../shared-utils";
import { ChecklistSectionFallback } from "../components/OfferChecklistFallbacks";
import { RichBenefitsSection } from "./RichBenefitsSection";

type Props = {
    /** The Core benefits section this one replaces; its title is kept (see RichBenefitsSection). */
    section: SalesChecklistSection;
    markdown: string;
};

/**
 * Same containment as ChecklistSectionWithErrorBoundary: a render failure here shows the section
 * fallback and emits the OfferChecklistError metric, instead of taking down the whole checklist step.
 */
export function RichBenefitsSectionWithErrorBoundary({ section, markdown }: Props): JSX.Element {
    const { logger: baseLogger, partner } = useSalesContext();
    const logger = baseLogger.child({ module: "RichBenefitsSectionWithErrorBoundary", supportTeam: "sales" });

    return (
        <ErrorBoundary
            onError={(error) => {
                logger.error({ error }, "Error rendering rich benefits section");
                void sendSalesErrorMetric("OfferChecklistError", {
                    eventSource: "Offer-Checklist",
                    partner,
                });
            }}
            fallbackRender={() => <ChecklistSectionFallback section={section} />}
        >
            <RichBenefitsSection title={section.title} markdown={markdown} />
        </ErrorBoundary>
    );
}
